import { useState } from 'react';
import './ArtistStatusPanel.css';

const formatCountdown = (endTime, state) => {
    if (state === 'not_started') return '—';
    if (!endTime || state === 'expired') return 'Expired';
    const ms = new Date(endTime).getTime() - Date.now();
    if (ms <= 0) return 'Expired';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h === 0 && m < 1) return '< 1m';
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
};

const notifLabel = (status) => {
    switch (status) {
        case 'sent':    return '✓ sent';
        case 'failed':  return '⚠ failed';
        case 'bounced': return '⚠ bounced';
        case 'pending': return 'pending';
        default:        return '—';
    }
};

function ArtistRow({ artist, onResend, resending }) {
    const { name, notificationStatus, timer, credits, sharedLinkId, canResend, accessCount } = artist;
    const countdown = formatCountdown(timer.endTime, timer.state);
    const isExpired = timer.state === 'expired' || (timer.endTime && new Date(timer.endTime) < new Date());

    return (
        <div className='asp-artist-row'>
            <span className='asp-artist-name'>{name}</span>
            <span className={`asp-notif asp-notif-${notificationStatus ?? 'pending'}`}>
                {notifLabel(notificationStatus)}
            </span>
            <span className={`asp-timer${isExpired ? ' asp-timer-expired' : ''}`}>
                {countdown}
            </span>
            <span className='asp-credits'>
                <span className='asp-credit-pending'>{credits.pending} Pending</span>
                <span className='asp-credit-approved'>{credits.approved + (credits.auto_approved ?? 0)} ✓ Approved</span>
                <span className='asp-credit-rejected'>{credits.rejected} ✗ Rejected</span>
            </span>
            <span className='asp-access-count' title='Times link opened'>
                {accessCount ?? 0} {accessCount === 1 ? 'open' : 'opens'}
            </span>
            {canResend && (
                <button
                    className='asp-resend-btn'
                    onClick={() => onResend(sharedLinkId)}
                    disabled={resending}
                >
                    {resending ? 'Sending…' : 'Resend'}
                </button>
            )}
        </div>
    );
}

export default function ArtistStatusPanel({ artistStatus, onResend }) {
    const [isOpen, setIsOpen] = useState(false);
    const [resending, setResending] = useState(null);
    const [resendError, setResendError] = useState('');

    const failedCount = artistStatus.filter(a =>
        (a.notificationStatus === 'failed' || a.notificationStatus === 'bounced') && a.canResend
    ).length;

    const handleResend = async (linkId) => {
        setResendError('');
        setResending(linkId);
        try {
            await onResend(linkId);
        } catch (err) {
            setResendError(`Resend failed: ${err.message}`);
        } finally {
            setResending(null);
        }
    };

    return (
        <div className='asp-panel'>
            <button className='asp-toggle' onClick={() => setIsOpen(o => !o)}>
                <span className='asp-toggle-label'>Artist Status</span>
                {failedCount > 0 && (
                    <span className='asp-alert-badge'>{failedCount} need resend</span>
                )}
                <span className='asp-chevron'>{isOpen ? '▼' : '▲'}</span>
            </button>

            {isOpen && (
                <div className='asp-content'>
                    {resendError && (
                        <div className='asp-resend-error'>
                            {resendError}
                            <button className='asp-error-dismiss' onClick={() => setResendError('')}>×</button>
                        </div>
                    )}
                    {artistStatus.length === 0 && (
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'var(--text-sm)', margin: 0 }}>
                            No artist data yet.
                        </p>
                    )}
                    {artistStatus.map(artist => (
                        <ArtistRow
                            key={artist.userId}
                            artist={artist}
                            onResend={handleResend}
                            resending={resending === artist.sharedLinkId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}